import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useEffect, useState, useContext } from 'react';
import { Now } from 'temporal-luxon';
import { LocaleCodes } from '@medable/i18n-supported-locales';
import { isRtl } from './i18n_utils';
import { MessageResource } from './message_resource';
const DEFAULT_LOCALE = LocaleCodes.en_US;
const DEFAULT_TIME_ZONE = Now.timeZone().toString();
const createDefaultMessageResource = (locale) => new MessageResource(locale, {});
const DEFAULT_MESSAGE_RESOURCE = createDefaultMessageResource(DEFAULT_LOCALE);
/** Provides to the current locale. */
export const LocaleContext = createContext(DEFAULT_LOCALE);
/** Allows to change the current locale. */
export const SetLocaleContext = createContext(null);
/** Provides the current time zone identifiers. */
export const TimeZoneContext = createContext({ user: undefined, os: DEFAULT_TIME_ZONE });
/** Allows to change the current user time zone. */
export const SetUserTimeZoneContext = createContext(null);
/** MessageResource for the curent locale. */
const MessageResourceContext = createContext(DEFAULT_MESSAGE_RESOURCE);
/** Provides a method to localize messages. */
export function useLocalizeMessage() {
    const messageResource = useContext(MessageResourceContext);
    return (source, params) => messageResource.resolve(source, params);
}
/** Provides I18n context fot localized UI. */
export const I18nContextProvider = ({ locale = DEFAULT_LOCALE, messageProvider = createDefaultMessageResource, children, }) => {
    const inheritedTimeZone = useContext(TimeZoneContext);
    const [currentLocale, setCurrentLocale] = useState(locale);
    const [messageResource, setMessageResource] = useState(messageProvider(locale));
    const [userTimeZone, setUserTimeZone] = useState(undefined);
    const effectiveTimeZone = {
        user: userTimeZone !== null && userTimeZone !== void 0 ? userTimeZone : inheritedTimeZone.user,
        os: inheritedTimeZone.os || DEFAULT_TIME_ZONE,
    };
    useEffect(() => {
        setMessageResource(messageProvider(currentLocale));
    }, [currentLocale, messageProvider]);
    // Keep the active locale in sync with the `locale` prop. The prop is the initial locale at mount
    // and remains the source of truth when a parent changes it at runtime (e.g. an app that derives
    // the locale from the authenticated identity). Without this, a changed prop would be ignored
    // until remount. In-session changes made through SetLocaleContext are unaffected because the prop
    // itself does not change in that case.
    useEffect(() => {
        setCurrentLocale(locale);
    }, [locale]);
    useEffect(() => {
        if (typeof document === 'undefined') {
            return undefined;
        }
        const previousDirection = document.documentElement.getAttribute('dir');
        document.documentElement.setAttribute('dir', isRtl(currentLocale) ? 'rtl' : 'ltr');
        return () => {
            if (previousDirection === null) {
                document.documentElement.removeAttribute('dir');
            }
            else {
                document.documentElement.setAttribute('dir', previousDirection);
            }
        };
    }, [currentLocale]);
    function updateLocale(newLocale) {
        const messageResource = messageProvider(newLocale);
        setCurrentLocale(newLocale);
        setMessageResource(messageResource);
    }
    return (_jsx(LocaleContext.Provider, { value: currentLocale, children: _jsx(SetLocaleContext.Provider, { value: (newLocale) => updateLocale(newLocale), children: _jsx(TimeZoneContext.Provider, { value: effectiveTimeZone, children: _jsx(SetUserTimeZoneContext.Provider, { value: setUserTimeZone, children: _jsx(MessageResourceContext.Provider, { value: messageResource, children: children }) }) }) }) }));
};
//# sourceMappingURL=i18n_context.js.map